import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FaChevronLeft } from "react-icons/fa";
import { LuLink, LuSparkles, LuFileText, LuFileSpreadsheet, LuArrowRight, LuX } from "react-icons/lu";
import { Alert, Snackbar } from "@mui/material";
import { fetchJobById } from "../../Redux/ActionCreators/jobsAction";
import {
    fetchCandidateInvites,
    fetchInviteHiringStatuses,
    resendCandidateInvite,
} from "../../Redux/ActionCreators/candidateAction";
import AddCandidatesTab from "./AddCandidatesTab";
import InvitedCandidatesTab from "./InvitedCandidatesTab";
import InviteLinksTab from "./InviteLinksTab";
import SmartSearchPanel from "./SmartSearchPanel";
import UploadCsvPanel from "./UploadCsvPanel";
import UploadResumesPanel from "./UploadResumesPanel";
import Chat from "../Chat/Chat";
import "./inviteCandidatesV2.scss";
import { useAppPermission } from "../../hooks/useAppPermission";
import { P } from "../../constants/rbac";

const DEFAULT_SUMMARY = { invited: 0, applied: 0, no_response: 0, total: 0 };

const FLOW_TABS = [
    { key: "links", label: "Invite candidate links", icon: <LuLink size={15} /> },
    { key: "smart", label: "Smart Search", icon: <LuSparkles size={15} />, requiresInvite: true },
    { key: "resume", label: "Upload resumes", icon: <LuFileText size={15} />, requiresInvite: true },
    { key: "csv", label: "Upload CSV", icon: <LuFileSpreadsheet size={15} />, requiresInvite: true },
];

// Standalone page mounted at /jobs/:jobId/invite. Owns the invite list + summary
// (GET /jobs/{job_id}/candidate-invites/) so candidates picked up in any of the
// three "Add candidates" flows (smart search / CSV / resumes) surface together
// in "Invited candidates". CSV/resume uploads flagged "already applied" go
// straight to job candidates and intentionally do not appear here.
//
// Also rendered as the last step of the create-job wizard (`inCreateFlow`),
// with `jobId` passed in: there it swaps the workspace tabs for a flat tab row
// led by the job's share links, and "Try it" replaces the tabs with the
// candidate chat inline.
const InviteCandidatesV2 = ({ jobId: jobIdProp, inCreateFlow = false, onInvited, onViewProgress } = {}) => {
    const { jobId: routeJobId } = useParams();
    const jobId = jobIdProp || routeJobId;
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { can } = useAppPermission();
    const canInviteCandidates = can(P.JOBS_CANDIDATE_CREATE);

    const job = useSelector((state) => state.jobs.latestJob);
    const [titleLoading, setTitleLoading] = useState(false);

    const [activeMainTab, setActiveMainTab] = useState("add"); // 'add' | 'invited'

    // ── Create-flow only ────────────────────────────────────────────────────
    const [flowTab, setFlowTab] = useState("links");
    const [tryingChat, setTryingChat] = useState(false);
    const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

    // Keep the page behind the candidate-preview modal from scrolling
    useEffect(() => {
        if (!tryingChat) return;
        const previous = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = previous; };
    }, [tryingChat]);

    // ── Invite list state (server-driven) ───────────────────────────────────
    const [invites, setInvites] = useState([]);
    const [summary, setSummary] = useState(DEFAULT_SUMMARY);
    const [invitesLoading, setInvitesLoading] = useState(false);
    const [hiringStatuses, setHiringStatuses] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [statusId, setStatusId] = useState("");
    const [pagination, setPagination] = useState(null);

    useEffect(() => {
        const fetchJobData = async () => {
            if (!jobId) return;
            setTitleLoading(true);
            await dispatch(fetchJobById(jobId));
            setTitleLoading(false);
        };
        fetchJobData();
    }, [dispatch, jobId]);

    useEffect(() => {
        if (!canInviteCandidates && !inCreateFlow) {
            navigate(jobId ? `/jobs/${jobId}/candidates` : "/jobs", { replace: true });
        }
    }, [canInviteCandidates, inCreateFlow, jobId, navigate]);

    useEffect(() => {
        let active = true;
        dispatch(fetchInviteHiringStatuses()).then((res) => {
            if (active && res?.success) setHiringStatuses(res.data || []);
        });
        return () => { active = false; };
    }, [dispatch]);

    const loadInvites = useCallback(async () => {
        if (!jobId) return;
        setInvitesLoading(true);
        const res = await dispatch(fetchCandidateInvites(jobId, { statusId: statusId || undefined, page, pageSize }));
        setInvitesLoading(false);
        if (res?.success) {
            setInvites(res.invites || []);
            setSummary(res.summary || DEFAULT_SUMMARY);
            setPagination(res.pagination || null);
        }
    }, [dispatch, jobId, statusId, page, pageSize]);

    useEffect(() => {
        loadInvites();
    }, [loadInvites]);

    // Called by every add flow once its API call resolves. Resets to page 1 so
    // the newest invites are visible, then refetches list + summary.
    const handleInvited = useCallback(() => {
        setPage(1);
        // If already on page 1 the loadInvites effect won't re-run on its own.
        loadInvites();
        onInvited?.();
    }, [loadInvites, onInvited]);

    const goToCandidates = useCallback(() => {
        navigate(`/jobs/${jobId}/candidates`);
    }, [navigate, jobId]);

    const handleResend = useCallback(async (invite) => {
        const inviteId = invite.id || invite.invite_id;
        const res = await dispatch(resendCandidateInvite(jobId, inviteId));
        if (res?.success) loadInvites();
        return res;
    }, [dispatch, jobId, loadInvites]);

    const invitedEmails = new Set(
        invites
            .map((inv) => (inv.candidate?.email || inv.candidate_email || "").toLowerCase())
            .filter(Boolean)
    );
    const companyName = job?.company_name || job?.organization?.name || job?.company || "";
    const totalCount = summary?.total ?? invites.length;

    if (inCreateFlow) {
        // latestJob may still hold the previously viewed job until the fetch lands
        const flowJob = job && String(job.id) === String(jobId) ? job : null;
        const visibleTabs = FLOW_TABS.filter((tab) => !tab.requiresInvite || canInviteCandidates);

        const goToProgress = () => {
            onViewProgress?.();
            navigate(`/jobs/${jobId}/candidates`, { state: { jobId } });
        };

        return (
            <div className="icv2-flow">
                <div className="icv2-flow-header">
                    <div>
                        <h2 className="icv2-flow-title">Invite Candidates</h2>
                        <p className="icv2-flow-subtitle">
                            {titleLoading || !flowJob
                                ? "Loading job…"
                                : `${flowJob.title || "Your job"} is live. Share it or add candidates to the pipeline.`}
                        </p>
                    </div>
                    <button className="icv2-flow-primary" onClick={goToProgress}>
                        View candidate progress <LuArrowRight size={16} />
                    </button>
                </div>

                {/* Candidate preview opens as a modal over the tabs; the backdrop
                    blocks the page but does not close it — only the × does. */}
                {tryingChat && (
                    <div className="icv2-chat-modal-overlay">
                        <div className="icv2-chat-modal" role="dialog" aria-modal="true" aria-labelledby="icv2-chat-modal-title">
                            <div className="icv2-chat-modal-head">
                                <h3 id="icv2-chat-modal-title">Experience as candidate</h3>
                                <button className="icv2-chat-modal-close" onClick={() => setTryingChat(false)} aria-label="Close">
                                    <LuX size={18} />
                                </button>
                            </div>
                            {/* <button className="icv2-flow-back" onClick={() => setTryingChat(false)}>
                                <LuArrowLeft size={15} /> Back to invite options
                            </button> */}
                            <Chat jobId={jobId} embedded />
                        </div>
                    </div>
                )}

                <div className="icv2-flow-tabs" role="tablist">
                    {visibleTabs.map((tab) => (
                        <button
                            key={tab.key}
                            role="tab"
                            aria-selected={flowTab === tab.key}
                            className={`icv2-flow-tab${flowTab === tab.key ? " icv2-flow-tab--active" : ""}`}
                            onClick={() => setFlowTab(tab.key)}
                        >
                            {tab.icon} {tab.label}
                        </button>
                    ))}
                </div>

                <div className="icv2-flow-panel">
                    {flowTab === "links" && (
                        flowJob ? (
                            <InviteLinksTab
                                job={flowJob}
                                onTryIt={() => setTryingChat(true)}
                                onNotify={(message, severity) => setSnack({ open: true, message, severity })}
                            />
                        ) : (
                            <div className="icv2-flow-loading">
                                <div className="spinner-border spinner-border-sm" role="status" style={{ color: "#F75891" }}>
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        )
                    )}
                    {flowTab === "smart" && (
                        <SmartSearchPanel jobId={jobId} invitedEmails={invitedEmails} onInvited={handleInvited} />
                    )}
                    {flowTab === "resume" && (
                        <UploadResumesPanel jobId={jobId} onInvited={handleInvited} />
                    )}
                    {flowTab === "csv" && (
                        <UploadCsvPanel jobId={jobId} onInvited={handleInvited} />
                    )}
                </div>

                <Snackbar
                    anchorOrigin={{ vertical: "top", horizontal: "center" }}
                    open={snack.open}
                    autoHideDuration={3000}
                    onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
                >
                    <Alert
                        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
                        severity={snack.severity}
                        sx={{ width: "100%" }}
                    >
                        {snack.message}
                    </Alert>
                </Snackbar>
            </div>
        );
    }

    return (
        <div className="icv2-wrap">
            <div className="icv2-header-bar">
                <button className="icv2-back-arrow" onClick={goToCandidates} title="Back to Candidates">
                    <FaChevronLeft />
                </button>

                <span className="icv2-eyebrow">Candidate intake workspace</span>

                {titleLoading ? (
                    <div className="spinner-border spinner-border-sm" role="status" style={{ color: "#F75891" }}>
                        <span className="visually-hidden">Loading...</span>
                    </div>
                ) : (
                    <>
                        <h1 className="icv2-workspace-title">{job?.title || "Untitled role"}</h1>
                        <p className="icv2-workspace-subtitle">
                            {companyName || "Your hiring team"}
                            <span>·</span>
                            Invite and track outreach
                        </p>
                    </>
                )}
{/*
                <button className="icv2-view-pipeline-link" onClick={goToCandidates}>
                    View pipeline →
                </button> */}
            </div>

            <div className="icv2-content">
                <div className="icv2-tabbar">
                    <button
                        className={`icv2-tabbar-btn${activeMainTab === "add" ? " icv2-tabbar-btn--active" : ""}`}
                        onClick={() => setActiveMainTab("add")}
                    >
                        Add candidates
                    </button>
                    <button
                        className={`icv2-tabbar-btn${activeMainTab === "invited" ? " icv2-tabbar-btn--active" : ""}`}
                        onClick={() => { setActiveMainTab("invited"); loadInvites(); }}
                    >
                        Invited candidates
                        <span className="icv2-tab-badge">{totalCount}</span>
                    </button>
                </div>

                <div className="icv2-tabpanel">
                    {activeMainTab === "add" ? (
                        <AddCandidatesTab
                            jobId={jobId}
                            jobTitle={job?.title}
                            invitedCount={totalCount}
                            invitedEmails={invitedEmails}
                            onInvited={handleInvited}
                        />
                    ) : (
                        <InvitedCandidatesTab
                            jobId={jobId}
                            invites={invites}
                            summary={summary}
                            loading={invitesLoading}
                            hiringStatuses={hiringStatuses}
                            page={page}
                            pageSize={pageSize}
                            statusId={statusId}
                            pagination={pagination}
                            onPageChange={setPage}
                            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                            onStatusChange={(id) => { setStatusId(id); setPage(1); }}
                            onResend={handleResend}
                            onRefresh={loadInvites}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default InviteCandidatesV2;
